// apiService.ts
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { config } from '../utils/globalImports';

const baseURL = config[process.env.NODE_ENV as keyof typeof config].apiUrl;

// Extend InternalAxiosRequestConfig for custom flags
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean; // To mark if the request is a retry attempt by the interceptor
}

const api = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 0, // 10 seconds
    withCredentials: true, // Ensure cookies are sent with requests
});

// Request interceptor (can add auth token if needed in the future)
api.interceptors.request.use(
    (config) => config,
    (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: AxiosError | null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(undefined); // Resolve with no specific value, original request will be retried
        }
    });
    failedQueue = [];
};

// Response interceptor for handling errors
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    // Check if the error is a 401 and the request is not already a retry and not for the refresh endpoint itself
    if (error.response?.status === 401 && !originalRequest._retry) {
        
        // If the request that failed with 401 is the refresh token request itself
        if (originalRequest.url?.endsWith('/auth/refresh')) {
            // This means the refresh token is invalid or expired.
            // At this point, you should probably redirect to login or handle global logout.
            // For now, we'll just reject the promise, which will be caught by ApiService methods.
            // You might want to add a global event dispatch here for logout. E.g.:
             window.dispatchEvent(new CustomEvent('authFailure'));
            isRefreshing = false; // Ensure this is reset
            processQueue(error); // Reject any pending requests in queue
            return Promise.reject(error);
        }

        if (isRefreshing) {
            // If a refresh is already in progress, add this request to a queue
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
            .then(() => {
                // After the refresh is done (successfully or not), retry the original request
                // The token should be updated in the cookies by the successful refresh
                return api(originalRequest);
            })
            .catch(queueError => {
                // If processing the queue resulted in an error (e.g. refresh failed)
                return Promise.reject(queueError || error);
            });
        }

        originalRequest._retry = true; // Mark that we are attempting a retry for this request
        isRefreshing = true;

        try {
            // Call refresh endpoint (will set new cookies on success)
            await api.get('/auth/refresh');
            
            // Refresh was successful, process the queue for other waiting requests
            processQueue(null);
            
            // Retry the original request that triggered the refresh
            return api(originalRequest);
        } catch (refreshError: any) {
            // Refresh failed
            processQueue(refreshError as AxiosError); // Notify queued requests about the failure
            
            // If refreshError itself is 401, means refresh token is bad.
            // Consider global logout action here too.
            // window.dispatchEvent(new CustomEvent('authFailure'));
            
            // Reject with the error from the refresh attempt, or the original error if refreshError is not an AxiosError
            return Promise.reject(refreshError instanceof AxiosError ? refreshError : error);
        } finally {
            isRefreshing = false;
        }
    }

    // For errors other than 401, or if it's a retried request that failed again,
    // or if it's an error on the /auth/refresh endpoint that wasn't handled above (e.g. network error)
    return Promise.reject(error);
  }
);

// Define and export ApiResponse interface
export interface ApiResponse<T> {
    data: T | null;
    error: string | null;
}

// Generic API methods
const ApiService = {
     // GET request
    get: async <T>(endpoint: string, params: Record<string, any> = {}): Promise<ApiResponse<T>> => {
        try {
            const response: AxiosResponse<T> = await api.get(endpoint, {params}); 
            return { data: response.data, error: null };
        } catch (error: any) {
             return {
                data: null,
                error: error.response?.data || 'An error occurred',
            };
        }
    },

    // POST request
    post: async <T, U>(endpoint: string, data: U,
    responseType: 'json' | 'blob' = 'json'
    ): Promise<ApiResponse<T>> => {
        try {
            const response: AxiosResponse<T> = await api.post(endpoint, data,{
            responseType, // axios will return blob if asked
        });
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data || 'An error occurred',
            };
        }
    },

    // PUT request
    put: async <T, U>(endpoint: string, data: U): Promise<ApiResponse<T>> => {
        try {
            const response: AxiosResponse<T> = await api.put(endpoint, data);
            return { data: response.data, error: null };
        } catch (error: any) {
           return {
                data: null,
                error: error.response?.data || 'An error occurred',
            };
        }
    },

    // DELETE request
    delete: async <T>(endpoint: string): Promise<ApiResponse<T>> => {
        try {
            const response: AxiosResponse<T> = await api.delete(endpoint);
            return { data: response.data, error: null };
        } catch (error: any) {
           return {
                data: null,
                error: error.response?.data || 'An error occurred',
            };
        }
    }
};

export default ApiService;